import React from "react";
import { DollarSign, TrendingUp, CheckCircle, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Comparison() {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-blue-700 flex items-center gap-2">
            <DollarSign size={24} /> CallAI CRM Cost Comparison
          </h1>
          <Link
            to="/pricing"
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow"
          >
            View Pricing Plans →
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-16">
        {/* Hero Section */}
        <section className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Close 2–3 More Real Estate Deals This Month
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Without making a single cold call. CallAI CRM automates 90% of your
            property outreach — qualifying leads and booking appointments while
            you focus on closing deals.
          </p>

          <div className="mt-6 flex justify-center gap-4">
            <Link
              to="/trial"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium shadow"
            >
              Start Free 7-Day Trial
            </Link>
            <Link
              to="/demo"
              className="border border-blue-600 text-blue-700 px-6 py-3 rounded-lg font-medium hover:bg-blue-50"
            >
              Watch Demo
            </Link>
          </div>
        </section>

        {/* Cost Comparison Table */}
        <section>
          <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            How CallAI CRM Outperforms Competitors
          </h3>

          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse text-sm text-gray-700">
              <thead className="bg-gray-100 text-gray-900">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">Feature</th>
                  <th className="py-4 px-6 font-semibold text-center">CallAI CRM</th>
                  <th className="py-4 px-6 font-semibold text-center">GoHighLevel</th>
                  <th className="py-4 px-6 font-semibold text-center">CallRail</th>
                  <th className="py-4 px-6 font-semibold text-center">PhoneBurner</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["AI Voice Calling & Booking", true, false, false, false],
                  ["Built-In CRM & Pipeline", true, true, false, false],
                  ["Unlimited Campaigns", true, false, false, false],
                  ["Real-Time Call Transcripts", true, false, true, false],
                  ["Automated Follow-Ups", true, true, false, true],
                  ["Integrated DNC Compliance", true, false, false, false],
                  ["Monthly Cost (USD)", "$147", "$497", "$600+", "$399"],
                ].map(([feature, callai, ghl, callrail, phoneburner], i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="py-3 px-6 font-medium text-gray-800">
                      {feature}
                    </td>
                    {[callai, ghl, callrail, phoneburner].map((val, j) => (
                      <td
                        key={j}
                        className="py-3 px-6 text-center font-medium"
                      >
                        {typeof val === "boolean" ? (
                          val ? (
                            <CheckCircle className="text-emerald-500 inline" size={18} />
                          ) : (
                            <XCircle className="text-red-400 inline" size={18} />
                          )
                        ) : (
                          <span
                            className={`${
                              val === "$147" ? "text-blue-600 font-semibold" : ""
                            }`}
                          >
                            {val}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-gray-500 text-sm mt-4">
            *Competitor pricing based on public 2025 plans. Feature availability may vary.
          </p>
        </section>

        {/* Savings Highlight */}
        <section className="text-center bg-blue-50 border border-blue-100 rounded-2xl py-12 px-8">
          <h3 className="text-2xl font-semibold text-blue-700 mb-3">
            Save Up to $4,200/Year vs Other Platforms
          </h3>
          <p className="text-gray-700 max-w-2xl mx-auto">
            Most investors using GoHighLevel + CallRail + PhoneBurner spend over{" "}
            <span className="font-bold text-gray-900">$500–$1,000/month</span>.
            CallAI CRM delivers the same power (plus AI calling) for{" "}
            <span className="font-bold text-blue-700">$147/month</span>.
          </p>

          <div className="mt-6 flex justify-center gap-4">
            <Link
              to="/trial"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium shadow"
            >
              Start Free 7-Day Trial
            </Link>
            <Link
              to="/pricing"
              className="border border-blue-600 text-blue-700 px-6 py-3 rounded-lg font-medium hover:bg-blue-50"
            >
              View Pricing →
            </Link>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold text-gray-900 mb-10">
            Real Estate Investors Closing More Deals
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Mike K.",
                location: "Dallas",
                text: "Closed 3 deals in my first month using CallAI CRM. ROI paid for itself 10x over.",
              },
              {
                name: "Sarah L.",
                location: "Miami",
                text: "I was spending 4 hours daily on cold calls. Now AI handles everything — total game changer.",
              },
              {
                name: "David J.",
                location: "Phoenix",
                text: "One AI call landed me a $40K wholesale deal. The system paid for itself instantly.",
              },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-xl shadow-sm p-6"
              >
                <p className="text-gray-700 italic mb-3">“{t.text}”</p>
                <p className="font-semibold text-blue-700">{t.name}</p>
                <p className="text-sm text-gray-500">{t.location}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-white border-t border-gray-200 py-6 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} CallAI CRM — Automated Real Estate Calling Platform
      </footer>
    </div>
  );
}
